import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import { isApiError } from '@/shared/api/errors';
import type { CursorPage } from '@/shared/api/types';
import { getAuthState } from '@/shared/auth/store';
import type { ReactionType } from '@/shared/components/reactions';
import { REALTIME_RECONNECTED, REALTIME_TOPIC_REVOKED, type RealtimeManager } from '@/shared/realtime';

import { feedApi, feedKeys, type FeedComment, type FeedCounters, type PostType } from '../api';

import { mergeReactionCounts, removeCachedPost, replaceCachedPost, updateCachedPost } from './cache';
import { useNewPostsStore } from './newPosts';

/* Realtime events of the feed (backend: Orbita.Modules.Feed.Integration.FeedRealtime). */
export const FEED_EVENTS = {
  postCreated: 'feed.post-created',
  postUpdated: 'feed.post-updated',
  postDeleted: 'feed.post-deleted',
  postRead: 'feed.post-read',
  commentCreated: 'feed.comment-created',
  commentUpdated: 'feed.comment-updated',
  commentDeleted: 'feed.comment-deleted',
  reactionsChanged: 'feed.reactions-changed',
} as const;

export const postTopic = (postId: string) => `post:${postId}`;

export interface PostCreatedEvent {
  postId: string;
  authorId: string;
  type: PostType;
  isPublic: boolean;
  groupIds: string[];
  createdAt: string;
}

export interface PostUpdatedEvent {
  postId: string;
  change: 'edited' | 'pinned' | 'unpinned' | 'poll';
}

export interface PostReadEvent {
  postId: string;
  userId: string;
  readCount: number;
}

export interface CommentEvent {
  postId: string;
  commentId: string;
  actorId: string;
  commentCount: number;
}

export interface ReactionsChangedEvent {
  postId: string;
  commentId: string | null;
  reactions: { type: ReactionType; count: number }[];
  actorId: string;
  actorReaction: ReactionType | null;
}

type CommentPages = InfiniteData<CursorPage<FeedComment>, string | null>;

const myId = () => getAuthState().user?.id;

/** Reloads a post after a change somebody else made; drops it when the reader can no longer see it. */
async function refreshPost(queryClient: QueryClient, postId: string): Promise<void> {
  try {
    replaceCachedPost(queryClient, await feedApi.post(postId));
  } catch (error) {
    if (isApiError(error) && error.status === 404) removeCachedPost(queryClient, postId);
  }
}

/**
 * Keeps the feed caches live (registered once from the module's `setup`): new posts feed the "N new posts" indicator
 * and the sidebar counter; edits, votes, pins, comments, reactions and reads patch or reload the posts on screen
 * (clients subscribe to `post:{id}` while a post is rendered).
 */
export function registerFeedRealtime(queryClient: QueryClient, realtime: RealtimeManager): () => void {
  const offs = [
    realtime.on<PostCreatedEvent>(FEED_EVENTS.postCreated, (event) => {
      if (event.authorId === myId()) return;
      useNewPostsStore.getState().add({ postId: event.postId, groupIds: event.groupIds });
      queryClient.setQueryData<FeedCounters>(feedKeys.counters, (counters) =>
        counters
          ? {
              newPosts: counters.newPosts + 1,
              unreadImportant: counters.unreadImportant + (event.type === 'important' ? 1 : 0),
            }
          : counters,
      );
      if (event.type === 'important')
        void queryClient.invalidateQueries({ queryKey: feedKeys.importantUnread });
    }),

    realtime.on<PostUpdatedEvent>(FEED_EVENTS.postUpdated, (event) => {
      if (event.change === 'pinned' || event.change === 'unpinned') {
        void queryClient.invalidateQueries({ queryKey: feedKeys.pinnedAll });
        void queryClient.invalidateQueries({ queryKey: feedKeys.streams });
      }
      void refreshPost(queryClient, event.postId);
    }),

    realtime.on<{ postId: string }>(FEED_EVENTS.postDeleted, (event) => {
      removeCachedPost(queryClient, event.postId);
      void queryClient.invalidateQueries({ queryKey: feedKeys.importantUnread });
      void queryClient.invalidateQueries({ queryKey: feedKeys.counters });
    }),

    realtime.on<PostReadEvent>(FEED_EVENTS.postRead, (event) => {
      const isMe = event.userId === myId();
      updateCachedPost(queryClient, event.postId, (post) =>
        post.important
          ? {
              ...post,
              important: {
                ...post.important,
                readCount: event.readCount,
                readByMe: post.important.readByMe || isMe,
              },
            }
          : post,
      );
      void queryClient.invalidateQueries({ queryKey: feedKeys.readers(event.postId) });
      if (isMe) {
        void queryClient.invalidateQueries({ queryKey: feedKeys.importantUnread });
        void queryClient.invalidateQueries({ queryKey: feedKeys.counters });
      }
    }),

    ...[FEED_EVENTS.commentCreated, FEED_EVENTS.commentUpdated, FEED_EVENTS.commentDeleted].map((type) =>
      realtime.on<CommentEvent>(type, (event) => {
        updateCachedPost(queryClient, event.postId, (post) =>
          post.commentCount === event.commentCount ? post : { ...post, commentCount: event.commentCount },
        );
        void queryClient.invalidateQueries({ queryKey: feedKeys.comments(event.postId) });
      }),
    ),

    realtime.on<ReactionsChangedEvent>(FEED_EVENTS.reactionsChanged, (event) => {
      const isMe = event.actorId === myId();
      if (!event.commentId) {
        updateCachedPost(queryClient, event.postId, (post) => ({
          ...post,
          reactions: mergeReactionCounts(post.reactions, event.reactions, isMe, event.actorReaction),
        }));
        void queryClient.invalidateQueries({ queryKey: feedKeys.reactions(event.postId) });
        return;
      }
      queryClient.setQueriesData<CommentPages>({ queryKey: feedKeys.comments(event.postId) }, (data) =>
        data
          ? {
              ...data,
              pages: data.pages.map((page) => ({
                ...page,
                items: page.items.map((comment) =>
                  comment.id === event.commentId
                    ? {
                        ...comment,
                        reactions: mergeReactionCounts(
                          comment.reactions ?? [],
                          event.reactions,
                          isMe,
                          event.actorReaction,
                        ),
                      }
                    : comment,
                ),
              })),
            }
          : data,
      );
    }),

    realtime.on<{ topic: string }>(REALTIME_TOPIC_REVOKED, ({ topic }) => {
      if (topic.startsWith('post:')) removeCachedPost(queryClient, topic.slice('post:'.length));
    }),

    // Events may have been missed while offline: refresh what is cheap to refresh.
    realtime.on(REALTIME_RECONNECTED, () => {
      void queryClient.invalidateQueries({ queryKey: feedKeys.counters });
      void queryClient.invalidateQueries({ queryKey: feedKeys.importantUnread });
      void queryClient.invalidateQueries({ queryKey: feedKeys.pinnedAll });
    }),
  ];
  return () => offs.forEach((off) => off());
}
